import styled, { useTheme } from 'styled-components'
import { USDC } from '@kunalabs-io/kai'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { Typography } from 'components/Typography/Typography'
import { getDisplayTimestamp, getHealthColor, getPositionStatusLabel } from 'utils/helpers'
import { getAprValue, getEquityUsdValue, getHealthValue, getPnlValue } from './utils'
import { Status } from './MyPositions'
import { IconButtonNext } from 'components/Icons/IconButtonNext'
import { useTokenPrice } from 'utils/useTokenPrice'
import { PositionRange } from './components/PositionRange'
import { ProtocolLpPool } from './components/ProtocolLpPool'
import { useRewardsFeesInfo } from 'modules/LpVaultDetails/hooks/useRewardFeeInfos'
import { useRewardsFeesTotalValue } from 'modules/LpVaultDetails/hooks/useRewardsFeesTotalValue'
import { usePositionMetrics } from 'modules/LpVaultDetails/Analytics/hooks/usePositionMetrics'
import { PositionDisplayInfo } from 'utils/userUserPositionDisplayInfos'

interface Props {
  index: number
  onClick?: (positionId: string) => void
  paginatedItems: PositionDisplayInfo[]
  activePositionId?: string
  displayInfo: PositionDisplayInfo
  positionInfo: PositionInfo
}

const RowWrapper = styled.div<{ $isLast?: boolean; $isActive?: boolean; onClick: (position: number) => void }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  padding: 0 24px;
  border-bottom: ${p => (p.$isLast ? 'none' : `1px solid ${p.theme.colors.divider}`)};
  ${p =>
    p.$isActive &&
    `
     background-color: ${p.theme.colors.background.active};
      & > div:last-child > svg > rect {
        fill: ${p.theme.colors.button.background};
      }
      & > div:last-child > svg > path {
        stroke: ${p.theme.colors.background.primary};

      }
    `}
  &:hover {
    background-color: ${p => p.theme.colors.background.active};
    & > div:last-child > svg > rect {
      fill: ${p => p.theme.colors.button.background};
    }
    & > div:last-child > svg > path {
      stroke: ${p => p.theme.colors.background.primary};
    }
  }
`

const Td = styled(Typography)`
  flex-basis: 200px;
  overflow: hidden;
  height: 64px;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: block;
    & > div {
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
    height: auto;
  }
`

const IconNextContainer = styled.div`
  display: flex;
  position: absolute;
  right: 24px;
  cursor: pointer;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    right: 16px;
  }
`

export const DesktopPositionRow = ({
  index,
  onClick,
  paginatedItems,
  activePositionId,
  displayInfo,
  positionInfo,
}: Props) => {
  const theme = useTheme()

  const { result: rewardsFeesInfos, isLoading: isLoadingRewardsFees } = useRewardsFeesInfo(positionInfo)
  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(positionInfo, rewardsFeesInfos, USDC)

  const { isLoading: isLoadingUsdX, price: priceUsdX } = useTokenPrice(displayInfo.position.X, USDC)
  const { isLoading: isLoadingUsdY, price: priceUsdY } = useTokenPrice(displayInfo.position.Y, USDC)

  const { isLoading: isLoadingPositionMetrics, data: positionMetrics } = usePositionMetrics(displayInfo.position.id)

  return (
    <RowWrapper
      key={index}
      $isLast={index === paginatedItems.length - 1}
      onClick={() => onClick?.(displayInfo.position.id)}
      $isActive={activePositionId === displayInfo.position.id}
    >
      <Td
        variant="body"
        style={{
          flexBasis: '150px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        {displayInfo.creationTimestampMs ? getDisplayTimestamp(displayInfo.creationTimestampMs) : ''}
      </Td>
      <Td variant="caption" fontWeight="medium" style={{ flexBasis: 100 }}>
        <ProtocolLpPool displayInfo={displayInfo} />
      </Td>
      <Td style={{ flexBasis: 100, fontSize: 12 }}>
        <Status $type={displayInfo.status}>{getPositionStatusLabel(displayInfo.status)}</Status>
      </Td>
      <Td
        variant="body"
        style={{
          flexBasis: 100,
          color: getHealthColor(Number(displayInfo.health?.mul(100).toFixed(0) || 0), theme),
        }}
      >
        {displayInfo.status === 'active' ? (
          getHealthValue(displayInfo.health!)
        ) : (
          <span style={{ color: theme.colors.text.primary }}>-</span>
        )}
      </Td>
      <Td
        variant="body"
        style={{
          flexBasis: '200px',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <PositionRange displayInfo={displayInfo} />
      </Td>

      <Td variant="body" fontWeight="bold" style={{ flexBasis: 150 }}>
        {getEquityUsdValue({
          status: displayInfo.status,
          isLoadingUsdX,
          priceUsdX,
          equityX: displayInfo.equity.x,
          isLoadingUsdY,
          priceUsdY,
          equityY: displayInfo.equity.y,
          isLoadingPendingRewards:
            !isLoadingRewardsFees && rewardsFeesInfos === null ? false : isLoadingRewardsFees || isLoadingTotalValue,
          pendingRewardsSum: totalSum,
        })}
      </Td>
      <Td variant="body" fontWeight="bold" style={{ flexBasis: 150 }}>
        {getAprValue(isLoadingPositionMetrics, displayInfo.status, positionMetrics?.apr1h.apr)}
      </Td>
      <Td variant="body" fontWeight="bold" style={{ flexBasis: 150 }}>
        {getPnlValue(isLoadingPositionMetrics, positionMetrics?.pnl.totalPnl)}
      </Td>
      <IconNextContainer>
        <IconButtonNext width={40} height={40} />
      </IconNextContainer>
    </RowWrapper>
  )
}
